import React, { useState } from 'react';

import { faBolt, faClipboard, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faBolt as faBoltRegular, faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ActivityList from './activity_list';
import ActivityModal from 'bundles/apex/activities/modal';
import Button from 'shared/button';
import CopyOverlay from 'components/copy_overlay';
import EmptyState from 'shared/empty_state';
import Filters from 'bundles/apex/activities/filters';
import LoadingSpinner from 'components/ui/loading_spinner';
import { buildActivity } from 'bundles/apex/activities/form/helpers';
import { useActivitiesState } from 'bundles/apex/activities/hooks';
import * as filterHelpers from './filters/helpers';
import type { Activity, Features, FilterOptions, PaginationData } from 'bundles/apex/activities/types';
import type { Paths } from 'bundles/apex/activities/hooks';
import type { Parent, ParentOption } from 'bundles/crm/types';
import Pagination from 'components/ui/pagination';
import classNames from 'classnames';
import type { ActivityFilters } from './filters/types';

export interface ActivitiesProps {
  activities: Activity[];
  importerProps: any;
  isBackofficeAdmin?: boolean;
  forUserId?: number;
  currentParent?: Parent | Parent[];
  features?: Features;
  options?: {
    parents?: ParentOption[];
  }
  filters?: boolean | ActivityFilters;
  filterOptions?: FilterOptions;
  header?: boolean;
  inboxAddress?: string;
  listClassName?: string;
  paginate?: boolean;
  paths?: { availableTags?: string, create?: string, latest?: string } & Paths,
  showParents?: boolean | 'path';
  smallCreateButton?: boolean;
  tagText?: string;
  onChange?: (activities: Activity[]) => void;
  onEmptyStateClick?: () => void;
  onPageDataChange?: (pageData: PaginationData) => void;
}

export default function Activities({
  activities: activitiesProp,
  currentParent,
  features,
  options,
  filters: showFilters,
  filterOptions,
  header,
  inboxAddress,
  listClassName,
  paginate,
  paths,
  showParents,
  smallCreateButton,
  forUserId,
  isBackofficeAdmin,
  tagText,
  onChange,
  onEmptyStateClick,
  onPageDataChange,
}: ActivitiesProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const {
    activities,
    loading,
    pageData,
    addActivity,
    removeActivity,
    replaceActivity,
    filters,
    changeFilters,
  } = useActivitiesState({ activities: activitiesProp, paginate, paths, onChange, onPageDataChange });
  const hasFilters = filterHelpers.hasFilters(filters);


  let onClick;
  if (onEmptyStateClick) {
    onClick = onEmptyStateClick;
  } else if (paths?.create) {
    onClick = () => setShowCreateModal(true);
  }

  return (
    <>
      {(activities.length === 0 && !loading && (!showFilters)) ? (
        <EmptyState
          bodyText="Get started by adding an activity"
          buttonIcon={faPlus}
          buttonText="Add activity"
          header="No Activities to Display"
          icon={faBolt}
          plainButton={false}
          onClick={onClick}
        />
      ) : (
        <div className="d-flex flex-column flex-grow-1 overflow-hidden">
          {header && (
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon className="me-2 text-primary" icon={faBoltRegular} size="lg" />
                <h5 className="my-3 me-2" key="header">Activities</h5>
                {inboxAddress && (
                  <CopyOverlay
                    className="text-muted"
                    dark
                    text={inboxAddress}
                    tooltip="Copy inbox address for this record"
                  >
                    <FontAwesomeIcon icon={faClipboard} />
                  </CopyOverlay>
                )}
              </div>
              {paths?.create && (
                smallCreateButton ? (
                  <Button
                    icon={faCirclePlus}
                    key="add"
                    label="Add Activity"
                    size="sm"
                    variant="outline-primary"
                    onClick={() => setShowCreateModal(true)}
                  />
                ) : (
                  <Button.Primary
                    icon={faPlus}
                    key="add"
                    label="New activity"
                    onClick={() => setShowCreateModal(true)}
                  />
                ))}
            </div>
          )}

          <Filters
            features={features}
            filterOptions={filterOptions}
            filters={filters}
            show={showFilters}
            onChange={changeFilters}
          />

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className={classNames(listClassName)}>
              <ActivityList
                activities={activities}
                currentParent={currentParent}
                forUserId={forUserId}
                hasFilters={hasFilters}
                isBackofficeAdmin={isBackofficeAdmin}
                options={options}
                showParents={showParents}
                tagText={tagText}
                onDelete={removeActivity}
                onSave={replaceActivity}
              />
            </div>
          )}

          {!loading && pageData && pageData.totalPages > 1 &&
            <Pagination
              currentPage={pageData.currentPage}
              totalPages={pageData.totalPages}
              onChange={page => changeFilters({ page })}
            />
          }
        </div>
      )}

      <ActivityModal
        activity={buildActivity(paths?.create, { availableTagsPath: paths?.availableTags })}
        header="Add activity"
        options={options}
        show={showCreateModal}
        tagText={tagText}
        onHide={() => setShowCreateModal(false)}
        onSave={(activity) => {
          addActivity(activity);
          setShowCreateModal(false);
        }}
      />
    </>
  );
}
